import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { selectTotal } from '../../store/slices/nouveauxAdherentsSlice';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [isChangingPage, setIsChangingPage] = useState(false);
  const total = useSelector(selectTotal);
  
  // Handle page change with loading state
  const handlePageChange = (page: number) => {
    if (page === currentPage || page < 1 || page > totalPages || isChangingPage) return;
    
    setIsChangingPage(true);
    onPageChange(page);
    
    // Reset loading state after a short delay
    setTimeout(() => {
      setIsChangingPage(false);
    }, 500);
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Always show first and last page
    // Show 2 pages before and after current page if possible
    const pageNumbers = [1];
    
    // Calculate start and end of the middle section
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust if we're near the start
    if (currentPage < 4) {
      endPage = 5;
    }
    
    // Adjust if we're near the end
    if (currentPage > totalPages - 3) {
      startPage = totalPages - 4;
    }
    
    // Add ellipsis if needed before middle section
    if (startPage > 2) {
      pageNumbers.push(-1); // -1 represents ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis if needed after middle section
    if (endPage < totalPages - 1) {
      pageNumbers.push(-2); // -2 represents ellipsis to differentiate from the first one
    }
    
    // Add last page if not included yet
    pageNumbers.push(totalPages);
    
    return pageNumbers;
  };
  
  if (totalPages <= 1) return null;
  
  const pageNumbers = getPageNumbers();
  
  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 w-full">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isChangingPage}
          className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
            currentPage === 1 || isChangingPage
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Précédent
        </button>
        <span className="text-sm text-gray-700">
          Page {currentPage} sur {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isChangingPage}
          className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
            currentPage === totalPages || isChangingPage
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Affichage <span className="font-medium">{total > 0 ? (currentPage - 1) * 10 + 1 : 0}</span> à{' '}
            <span className="font-medium">{Math.min(currentPage * 10, total)}</span> sur{' '}
            <span className="font-medium">{total}</span> résultats
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || isChangingPage}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                currentPage === 1 || isChangingPage
                  ? 'cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            
            {pageNumbers.map((pageNumber, index) => {
              if (pageNumber < 0) {
                // Render ellipsis
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                  >
                    ...
                  </span>
                );
              }
              
              const isActive = pageNumber === currentPage;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  disabled={isActive || isChangingPage}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    isActive
                      ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || isChangingPage}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                currentPage === totalPages || isChangingPage
                  ? 'cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination; 